<template>
  <div class="flex flex-col justify-start items-stretch grow overflow-y-hidden">
    <div
      class="flex justify-start items-center gap-8 bg-neutral-primary p-2 rounded-t-md"
    >
      <div
        v-if="!isEmpty(chain.allowOpeningOrder)"
        class="flex flex-col justify-start items-start"
      >
        <NText
          variant="overline"
          weight="regular"
          class="text-neutral-secondary"
        >
          {{ t('option.chainStatus') }}
        </NText>

        <div class="flex items-center gap-1">
          <NText
            :class="chainStatus.labelClass"
            variant="overline"
            weight="bold"
          >
            {{ chainStatus.label }}
          </NText>

          <NegarInfoWithBorderIcon
            v-if="chain.allowOpeningOrder === CHAIN_STATUS.AT_RISK.value"
            size="14"
            class="cursor-pointer"
            :title="t('option.chainStatus')"
            :aria-label="t('option.chainStatus')"
            @click.stop="openChainStatusInfoDialog()"
          />
        </div>
      </div>

      <div class="flex flex-col justify-start items-start">
        <NText
          variant="overline"
          weight="regular"
          class="text-neutral-secondary"
        >
          {{ t('option.positionSameGroup') }}
        </NText>

        <NumberFormatter #="{ number }" :value="chain.numberOfOpenPosition">
          <NText variant="overline" weight="bold" dir="ltr">
            {{ number }}
          </NText>
        </NumberFormatter>
      </div>

      <div
        v-if="chain.openPositionUpperLimit"
        class="flex flex-col justify-start items-start"
      >
        <NText
          variant="overline"
          weight="regular"
          class="text-neutral-secondary"
        >
          {{ t('option.openPositionUpperLimit') }}
        </NText>

        <NumberFormatter #="{ number }" :value="chain.openPositionUpperLimit">
          <NText variant="overline" weight="bold" dir="ltr">
            {{ number }}
          </NText>
        </NumberFormatter>
      </div>

      <div
        v-if="chain.openPositionLowerLimit"
        class="flex flex-col justify-start items-start"
      >
        <NText
          variant="overline"
          weight="regular"
          class="text-neutral-secondary"
        >
          {{ t('option.openPositionLowerLimit') }}
        </NText>

        <NumberFormatter #="{ number }" :value="chain.openPositionLowerLimit">
          <NText variant="overline" weight="bold" dir="ltr">
            {{ number }}
          </NText>
        </NumberFormatter>
      </div>

      <div
        v-if="!isEmpty(chain.allowOpeningOrder)"
        class="flex flex-col justify-start items-start"
      >
        <NText
          variant="overline"
          weight="regular"
          class="text-neutral-secondary"
        >
          {{ t('option.maximumOpenPosition') }}
        </NText>

        <NumberFormatter #="{ number }" :value="numberOfOpenPositions">
          <NText variant="overline" weight="bold" dir="ltr">
            {{ number }}
          </NText>
        </NumberFormatter>
      </div>
    </div>

    <div
      class="w-full flex justify-start items-stretch border-t border-neutral-primary"
    >
      <div
        v-if="viewMode.value !== 2"
        :class="[
          'flex justify-center bg-brand-secondary basis-[calc(50%_-_3rem)] py-1',
          { 'basis-full': viewMode.value === 1 },
        ]"
      >
        <NText variant="caption2" weight="medium" class="text-brand-primary">
          {{ t('option.buy') }}
        </NText>
      </div>

      <div
        v-if="viewMode.value === 0"
        class="flex justify-center basis-[6rem] bg-neutral-primary"
      />

      <div
        v-if="viewMode.value !== 1"
        :class="[
          'flex justify-center bg-critical-secondary basis-[calc(50%_-_3rem)] py-1',
          { 'basis-full': viewMode.value === 2 },
        ]"
      >
        <NText variant="overline" weight="medium" class="text-critical-primary">
          {{ t('option.sell') }}
        </NText>
      </div>
    </div>

    <div
      class="flex justify-start items-start w-full grow bg-neutral-primary overflow-y-auto"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { isEmpty } from '@libs/utils';
import { NegarInfoWithBorderIcon } from '@negar/icons';

import NumberFormatter from '@/components/Number.vue';
import { useOptionDialogs } from '@/modules/option';

import { TViewMode } from './index.vue';
import { OptionContractChain } from '@libs/api-reference';
import { CHAIN_STATUS } from '../../constants.js';

const { t } = useI18n();

const { openChainStatusInfoDialog } = useOptionDialogs();

const props = defineProps<{
  isin: string;
  chain: OptionContractChain;
  viewMode: TViewMode;
}>();

const numberOfOpenPositions = computed(() =>
  Number(
    (props?.chain?.openPositionUpperLimit || 0) -
      (props?.chain?.numberOfOpenPosition || 0)
  )
);

const chainStatus = computed(
  () =>
    Object.values(CHAIN_STATUS).find(
      (status) => status.value === props.chain.allowOpeningOrder
    )!
);
</script>
